import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GroupedPanelList,
  IconButton,
  Item,
  MoreFiltersChip,
  MultiselectChip,
  SearchField,
} from "@deere/isg.component-library";
import { getNavigatorState } from "../../../modules/navigatorStateHelper";
import { getMachineDisplayState } from "../../../modules/machineStateDisplay";
import {
  Crawler,
  Dumptruck,
  Excavator,
  IconAdd,
  IconChevronRight,
  IconRecenter,
  Loader,
  SkidSteer,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
  Truck,
} from "@deere/icons/new";
import { useMap } from "@vis.gl/react-google-maps";
import { useDeferredFitBounds } from "../../hooks/useDeferredFitBounds";
import TickingTime from "../../components/TickingTime";

import "./EquipmentList.css";

// Locally define EquipmentPanelItem type
export type EquipmentPanelItem = Item & {
  vehiclezoombounds?: any;
};

function getStringTime(unixTime: number) {
  let formattedTime = "";
  if (unixTime) {
    const elapsed = Math.floor(unixTime / 1000); // Get elapsed time in seconds
    const days = Math.floor(elapsed / (3600 * 24));
    const hours = Math.floor((elapsed % (3600 * 24)) / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (days > 0) {
      formattedTime = `${days} days ${hours} hr`;
    } else if (hours > 0) {
      formattedTime = `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min ${seconds} sec`;
    } else {
      formattedTime = `${seconds} sec`;
    }
  } else {
    // formattedTime = `0 sec`;
    // console.log("Error, unix time unknown: ", unixTime);
    formattedTime = `0 sec`;
  }
  return formattedTime;
}

function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  if (lat1 == lat2 && lon1 == lon2) {
    return 0;
  } else {
    var radlat1 = (Math.PI * lat1) / 180;
    var radlat2 = (Math.PI * lat2) / 180;
    var theta = lon1 - lon2;
    var radtheta = (Math.PI * theta) / 180;
    var dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist;
  }
}

function EquipmentList({
  setCurrent,
  equipmentList,
  vehicleIconClicked,
  vehicleData,
  userLocation,
  subPanel,
  showCabControl,
}: {
  setCurrent: (item: EquipmentPanelItem, i: number) => void;
  equipmentList: any;
  vehicleIconClicked: boolean;
  vehicleData: any;
  userLocation: any;
  subPanel: string;
  showCabControl?: boolean;
}) {
  const map = useMap("mainMap"); // Get the map instance
  const deferredFitBounds = useDeferredFitBounds();

  const Dusty: Item = {
    //fake example data
    id: "0",
    title: "DUSTY",
    subtitle: "1DW460EBALF705342",
    icon: (
      <Dumptruck style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
    ),
  };

  const [activeItem, setActiveItem] = useState(-1);

  useEffect(() => {
    if (subPanel === "empty") {
      setActiveItem(-1);
    }
  }, [subPanel]);

  const [activeEquipment, setActiveEquipment] = useState<Item[]>([]); //holds active equipment
  const [inactiveEquipment, setInactiveEquipment] = useState<Item[]>([]); //hods inactive equipment
  const [filteredActiveEquipment, setFilteredActiveEquipment] = useState<Item[]>([
    ...activeEquipment,
  ]); //holds active filtered equipment
  const [filteredInactiveEquipment, setFilteredInactiveEquipment] = useState<Item[]>([
    ...inactiveEquipment,
  ]); //holds inactive filtered equipment
  const [favoritedItems, setFavoritedItems] = useState(
    new Array(activeEquipment.length + inactiveEquipment.length).fill(0),
  ); //zeroed list to store if each item is favorited or not
  const [currentEquipment, setCurrentEquipment] = useState<any>(null);

  useEffect(() => {
    // console.log("activeEquipment updated!!!");
    setFilteredActiveEquipment(activeEquipment);
  }, [activeEquipment]);

  useEffect(() => {
    // console.log("activeEquipment updated!!!");
    setFilteredInactiveEquipment(inactiveEquipment);
  }, [inactiveEquipment]);

  useEffect(() => {
    // console.log("filteredActiveEquipment updated!!!");
  }, [filteredActiveEquipment]);

  const Vehicle: Item = {
    id: "0",
    title: "DUSTY",
    subtitle: "1DW460EBALF705342",
    icon: (
      <Dumptruck style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
    ),
  };

  function updateActiveAndInactiveEquipment(equipmentList: { [key: string]: any }) {
    const vehicleArray: any[] = [];
    const vehicleArrayInactive: any[] = [];

    const Dusty: Item = {
      //fake data
      id: "0",
      title: "DUSTY",
      subtitle: "1DW460EBALF705342",
      icon: (
        <Dumptruck style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
      ),
    };
    // vehicleArray.push(Dusty);

    // Iterate through each child object in equipmentList
    for (const key in equipmentList) {
      if (equipmentList.hasOwnProperty(key)) {
        const equipment = equipmentList[key];

        // console.log("");console.log("");console.log("");console.log("");console.log("");
        // console.log(equipment.data);
        // console.log("");console.log("");console.log("");console.log("");console.log("");

        // Getting the autonomy status
        // Autonomy State
        let currentState = Number((equipment.data as any)?.VADC?.autonomy?.state?.[0]);
        // console.log("currentState: ", currentState);
        // Navigator State
        let navigatorState = getNavigatorState((equipment.data as any)?.Navigator?.NavigatorState);
        const { displayState } = getMachineDisplayState(equipment.data);
        // console.log("navigatorState: ", navigatorState);
        // Time since the last autonomy event
        let timeSince = "16 mins";

        // if (currentState == 0) {
        //     // Autonomy State: OFF
        //     setAutonomyStatus("OFF");
        // } else if (currentState == 1) {
        //     // Autonomy State: STANDBY
        //     setAutonomyStatus("STANDBY");
        // } else if (currentState == 2) {
        //     // Autonomy State: ACTIVE
        //     setAutonomyStatus("ACTIVE");
        // } else if (currentState == 3) {
        //     // Autonomy State: LOW_ENERGY_STATE
        //     setAutonomyStatus("LOW_ENERGY_STATE");
        // } else if (currentState == 255) {
        //     // Autonomy State: INVALID
        //     setAutonomyStatus("INVALID");
        // } else {
        //     // Autonomy State: Unknown by Orchestrator
        //     setAutonomyStatus("Unknown");
        // }
        // if (navigatorState == "OFF") {
        //     // Autonomy State: OFF
        //     setNavigatorStatus("OFF");
        // } else if (navigatorState == "GENERATING_PATH") {
        //     // Autonomy State: STANDBY
        //     setNavigatorStatus("GENERATING_PATH");
        // } else if (navigatorState == "READY") {
        //     // Autonomy State: ACTIVE
        //     setNavigatorStatus("READY");
        // } else if (navigatorState == "ENGAGED") {
        //     // Autonomy State: LOW_ENERGY_STATE
        //     setNavigatorStatus("ENGAGED");
        // } else if (navigatorState == "INTERRUPTED") {
        //     // Autonomy State: INVALID
        //     setNavigatorStatus("INTERRUPTED");
        // } else {
        //     // Autonomy State: Unknown by Orchestrator
        //     setNavigatorStatus("OFF");
        // }

        // Create bounds for map selection
        const bounds = new google.maps.LatLngBounds();
        // line.points.forEach((point: any) => {
        //     bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
        // });

        <>
          <Dumptruck
            style={{
              position: "relative",
              top: "22px",
              minHeight: "45px",
              minWidth: "45px",
              height: "auto",
              width: "auto",
            }}
          />
          <div style={{ display: "flex", alignItems: "center" }}>
            <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
            <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
          </div>
        </>;

        // generating the Icon
        let tempIcon = (
          <Dumptruck
            style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }}
          />
        );
        // let tempIcon = <>
        //         <Dumptruck style={{
        //             minHeight: "48px",
        //             minWidth: "48px",
        //             height: "48px",
        //             width: "48px"
        //         }} />
        //         <div style={{ position: "absolute", display: "flex", alignItems: "center" }}>
        //             <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
        //             <StatusEquipmentWorking size={20} sx={{ marginRight: '4px' }} />
        //         </div>
        //     </>;
        if (equipment.vehicleType === "DEX") {
          tempIcon = (
            <Excavator
              style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }}
            />
          );
        } else if (equipment.vehicleType === "CRW") {
          tempIcon = (
            <Crawler
              style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }}
            />
          );
        } else if (equipment.vehicleType === "CTL") {
          tempIcon = (
            <SkidSteer
              style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }}
            />
          );
        } else if (equipment.vehicleType === "TRK") {
          tempIcon = (
            <Truck style={{ minHeight: "32px", minWidth: "48px", height: "auto", width: "auto" }} />
          );
        } else if (equipment.vehicleType === "WDL") {
          tempIcon = (
            <Loader
              style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }}
            />
          );
        }

        let autonomyTimestamp = Number(
            (equipment.data as any)?.autonomyLog?.[
              Object.keys((equipment.data as any)?.autonomyLog).length - 1
            ]?.timestamp,
          ) || 0;
        // console.log("Time Object: ", (equipment.data as any)?.autonomyLog?.vadc?.[Object.keys((equipment.data as any)?.autonomyLog?.vadc).length - 1]?.timestamp);
        // if (timeSinceState) {
        //     const elapsed = Math.floor((Date.now() - timeSinceState) / 1000); // Get elapsed time in seconds
        //     const hours = Math.floor(elapsed / 3600);
        //     const minutes = Math.floor((elapsed % 3600) / 60);
        //     const seconds = elapsed % 60;
        //     if (hours > 0) {
        //         formattedTime = `${hours} hr`;
        //     } else if (minutes > 0) {
        //         formattedTime = `${minutes} min`;
        //     } else if (seconds < 15) {
        //         formattedTime = `As of now...`;
        //     } else {
        //         formattedTime = `${seconds} sec`;
        //     }
        // } else {
        //     formattedTime = `Unknown...`;
        // }
        // console.log("Time Since: ",timeSinceState);

        // console.log("currentState: ", currentState);
        // console.log("currentState: ", currentState, "Type: ", typeof currentState);
        // console.log("(currentState !== 1)", ((currentState as number) !== 1));
        // console.log("(currentState !== 2)", (currentState !== 2));
        // console.log("((currentState !== 1) && (currentState !== 2))", ((currentState !== 1) && (currentState !== 2)));
        // console.log("(currentState == 1)", (currentState == 1));
        // console.log("(currentState == 2 && navigatorState == INTERRUPTED)", (currentState == 2 && navigatorState == "INTERRUPTED"));
        // console.log("(currentState == 2 && navigatorState !== INTERRUPTED)", (currentState == 2 && navigatorState !== "INTERRUPTED"));

        let tempAutonomyStatus = (
          <>
            <div
              style={{
                position: "relative",
                left: "-92px",
                width: "200px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <b style={{ height: "16px" }}>
                {equipment.vehicleType + " " + equipment.vehicleName}
              </b>
              <div style={{ height: "18px" }}>{equipment.vehicleModel}</div>
              <div style={{ display: "flex", alignItems: "center" }}>
                {displayState === "off" && (
                  <div
                    style={{
                      width: "68px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      marginRight: "-23px",
                    }}
                  >
                    <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
                    Off
                  </div>
                )}
                {displayState === "ready" && (
                  <div
                    style={{
                      width: "60px",
                      color: "#00B600",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <StatusAutonomyReady size={20} />
                    Ready
                  </div>
                )}
                {displayState === "paused" && (
                  <div
                    style={{
                      color: "rgb(255, 120, 0)",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                    <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
                    Paused
                  </div>
                )}
                {displayState === "working" && (
                  <div
                    style={{
                      color: "#00B600",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                    <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
                    Working
                  </div>
                )}
                <div style={{ marginLeft: "6px", color: "#767676" }}><TickingTime timestamp={autonomyTimestamp} /></div>
              </div>
            </div>

            {/* <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ color: "#00B600", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                    <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
                                    <StatusEquipmentWorking size={20} sx={{ marginRight: '4px' }} />
                                    Working
                                </div>
                                <div style={{ marginLeft: "6px", color: "#767676" }}>{"16 mins"}</div>
                            </div> */}
          </>
        );

        let thisLat = null;
        let thisLng = null;
        try {
          // Check if the different location formats on the vehicles
          if (
            equipment.data.MachineState !== undefined &&
            equipment.data.MachineState.GlobalPose !== undefined
          ) {
            thisLat = equipment.data.MachineState.GlobalPose.location.latitude;
            thisLng = equipment.data.MachineState.GlobalPose.location.longitude;
          } else if (equipment.data.front_axle_center.GlobalPose !== undefined) {
            thisLat = equipment.data.front_axle_center.GlobalPose.location.latitude;
            thisLng = equipment.data.front_axle_center.GlobalPose.location.longitude;
          }
        } catch (error) {
          // console.log(error);
        }

        // Create bounds for map selection
        const vehicleZoomBounds = new google.maps.LatLngBounds();
        if (thisLat !== null && thisLng !== null) {
          vehicleZoomBounds.extend(new google.maps.LatLng(thisLat + 0.0005, thisLng + 0.0005));
          vehicleZoomBounds.extend(new google.maps.LatLng(thisLat - 0.0005, thisLng - 0.0005));
        }

        if (displayState === "off") {
          // Machine is inactive
          vehicleArrayInactive.push({
            // ...Dusty,
            // id: equipment.vehicleType,
            id: key,
            // title: equipment.vehicleType + ' ' + equipment.vehicleName, // You can customize the title based on the equipment
            // title: key,
            // subtitle: equipment.vehicleModel, // Use vehicleModel as subtitle or customize as needed
            extraContent: tempAutonomyStatus,
            icon: tempIcon,
            // equipmentData: equipment.data,
            latlng: { lat: thisLat, lng: thisLng },
            vehiclezoombounds: vehicleZoomBounds,
          });
        } else {
          // Machine is active
          vehicleArray.push({
            // ...Dusty,
            // id: equipment.vehicleType,
            id: key,
            // title: equipment.vehicleType + ' ' + equipment.vehicleName, // You can customize the title based on the equipment
            // title: key,
            // subtitle: equipment.vehicleModel, // Use vehicleModel as subtitle or customize as needed
            extraContent: tempAutonomyStatus,
            icon: tempIcon,
            // equipmentData: equipment.data,
            latlng: { lat: thisLat, lng: thisLng },
            vehiclezoombounds: vehicleZoomBounds,
          });
        }
      }
    }

    // return vehicleArray;
    setActiveEquipment(vehicleArray);
    setInactiveEquipment(vehicleArrayInactive);
  }

  useEffect(() => {
    if (equipmentList !== undefined && Object.keys(equipmentList).length > 0) {
      // setActiveEquipment(createDustyArray(equipmentList));

      updateActiveAndInactiveEquipment(equipmentList);
      // console.log(activeEquipment);
    }
  }, [equipmentList]);

  const handleStar = useCallback(
    (ix: number) => {
      //favorites items
      const newFavoritedItems = favoritedItems.map((c, i) => {
        if (i === ix) {
          return !c;
        } else {
          return c;
        }
      });
      setFavoritedItems(newFavoritedItems);
      // console.log("Updated " + ix);
    },
    [favoritedItems],
  );

  const handleItemSelect = useCallback(
    (item: Item, i: number) => {
      //changes active item
      setActiveItem(i);
      setCurrent(item, i);
      setCurrentEquipment(item);
    },
    [activeItem],
  );

  const groups = useMemo(
    () => {
      // Sort by stable equipment id so row order is stable across full-state dataUpdates.
      const sortedActive = [...filteredActiveEquipment].sort((a, b) =>
        String(a.id).localeCompare(String(b.id)),
      );
      const sortedInactive = [...filteredInactiveEquipment].sort((a, b) =>
        String(a.id).localeCompare(String(b.id)),
      );
      return [
      {
        //creates groups for Panel List. Check ISG UI Component for more

        title: "Active - Autonomous (" + sortedActive.length + ")",
        items: sortedActive.map((item, i) => {
          let distanceString = "-- mi";
          if (
            userLocation &&
            (item as any).latlng &&
            (item as any).latlng.lat !== null &&
            (item as any).latlng.lng !== null
          ) {
            const dist = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              (item as any).latlng.lat,
              (item as any).latlng.lng,
            );
            distanceString = dist.toFixed(1) + " mi";
          }
          return {
            ...item,
            active: i === activeItem,
            onClick: () => {
              // console.log((item as any));
              // console.log((item as any).latlng);
              if (!showCabControl) deferredFitBounds((item as any).vehiclezoombounds);
              handleItemSelect(item, i);
            },
            secondaryAction: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#767676",
                }}
              >
                <IconButton
                  className="distanceLabel"
                  icon={<IconRecenter primary={"#767676"} />}
                  style={{ width: "32px", height: "32px", pointerEvents: "none" }}
                  variant="borderless"
                />
                {distanceString}
                <IconButton
                  className="starButton"
                  icon={
                    favoritedItems[i] ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path d="M5.175 8.11875L7.25625 9.9375L6.6375 12.6188L9 11.1938L11.3625 12.6375L10.7437 9.9375L12.825 8.1375L10.0875 7.89375L9 5.34375L7.9125 7.875Z" />
                        <path
                          d="M4.36875 15.75L5.5875 10.4813L1.5 6.9375L6.9 6.46875L9 1.5L11.1 6.46875L16.5 6.9375L12.4125 10.4813L13.6312 15.75L9 12.9562L4.36875 15.75Z"
                          fill="#767676"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M6.6375 12.6188L9 11.1938L11.3625 12.6375L10.7437 9.9375L12.825 8.1375L10.0875 7.89375L9 5.34375L7.9125 7.875L5.175 8.11875L7.25625 9.9375L6.6375 12.6188ZM4.36875 15.75L5.5875 10.4813L1.5 6.9375L6.9 6.46875L9 1.5L11.1 6.46875L16.5 6.9375L12.4125 10.4813L13.6312 15.75L9 12.9562L4.36875 15.75Z"
                          fill="#767676"
                        />
                      </svg>
                    )
                  }
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                  onClick={() => handleStar(i)}
                />

                <IconButton
                  className="chevronButton"
                  icon={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconChevronRight size={24} />
                    </span>
                  }
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                />
              </div>
            ),
          };
        }),
      },
      {
        title: "Inactive (" + sortedInactive.length + ")",
        items: sortedInactive.map((item, i) => {
          let distanceString = "-- mi";
          if (
            userLocation &&
            (item as any).latlng &&
            (item as any).latlng.lat !== null &&
            (item as any).latlng.lng !== null
          ) {
            const dist = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              (item as any).latlng.lat,
              (item as any).latlng.lng,
            );
            distanceString = dist.toFixed(1) + " mi";
          }
          return {
            ...item,
            active: i + sortedActive.length === activeItem,
            onClick: () => handleItemSelect(item, i + sortedActive.length),
            secondaryAction: (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "#767676",
                }}
              >
                <IconButton
                  className="distanceLabel"
                  icon={<IconRecenter primary={"#767676"} />}
                  style={{ width: "32px", height: "32px", pointerEvents: "none" }}
                  variant="borderless"
                />
                {distanceString}
                <IconButton
                  className="starButton"
                  icon={
                    favoritedItems[i + sortedActive.length] ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path d="M5.175 8.11875L7.25625 9.9375L6.6375 12.6188L9 11.1938L11.3625 12.6375L10.7437 9.9375L12.825 8.1375L10.0875 7.89375L9 5.34375L7.9125 7.875Z" />
                        <path
                          d="M4.36875 15.75L5.5875 10.4813L1.5 6.9375L6.9 6.46875L9 1.5L11.1 6.46875L16.5 6.9375L12.4125 10.4813L13.6312 15.75L9 12.9562L4.36875 15.75Z"
                          fill="#767676"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M6.6375 12.6188L9 11.1938L11.3625 12.6375L10.7437 9.9375L12.825 8.1375L10.0875 7.89375L9 5.34375L7.9125 7.875L5.175 8.11875L7.25625 9.9375L6.6375 12.6188ZM4.36875 15.75L5.5875 10.4813L1.5 6.9375L6.9 6.46875L9 1.5L11.1 6.46875L16.5 6.9375L12.4125 10.4813L13.6312 15.75L9 12.9562L4.36875 15.75Z"
                          fill="#767676"
                        />
                      </svg>
                    )
                  }
                  style={{ width: "32px", height: "48px" }}
                  variant="borderless"
                  onClick={() => handleStar(i + sortedActive.length)}
                />

                <IconButton
                  className="chevronButton"
                  icon={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconChevronRight size={24} />
                    </span>
                  }
                  style={{ width: "32px", height: "32px" }}
                  variant="borderless"
                />
              </div>
            ),
          };
        }),
      },
    ];
    },
    [
      activeItem,
      activeEquipment,
      inactiveEquipment,
      filteredActiveEquipment,
      filteredInactiveEquipment,
      favoritedItems,
      userLocation,
    ],
  );

  function searchEquipment(e: any) {
    setFilteredActiveEquipment(
      activeEquipment.filter((item: any) =>
        item.title.toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()),
      ),
    );
    setFilteredInactiveEquipment(
      inactiveEquipment.filter((item: Item) =>
        item.title.toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()),
      ),
    );
  }

  function prepareVehicleItem(vehicleData: any): any {
    const key = `${vehicleData.vehicleType}_${vehicleData.vehicleModel}_${vehicleData.vehicleName}`;
    const currentState = Number(vehicleData?.data?.VADC?.autonomy?.state?.[0]);
    const navigatorState = getNavigatorState(vehicleData?.data?.Navigator?.NavigatorState);
    const { displayState } = getMachineDisplayState(vehicleData?.data);
    let lat = null;
    let lng = null;
    if (vehicleData?.data?.MachineState?.GlobalPose?.location) {
      lat = vehicleData.data.MachineState.GlobalPose.location.latitude;
      lng = vehicleData.data.MachineState.GlobalPose.location.longitude;
    } else if (vehicleData?.data?.front_axle_center?.GlobalPose?.location) {
      lat = vehicleData.data.front_axle_center.GlobalPose.location.latitude;
      lng = vehicleData.data.front_axle_center.GlobalPose.location.longitude;
    }
    const timeSinceState =
      Date.now() -
      Number(
        vehicleData?.data?.autonomyLog?.[Object.keys(vehicleData?.data?.autonomyLog).length - 1]
          ?.timestamp,
      );
    const formattedTime = getStringTime(timeSinceState);
    let tempIcon = (
      <Dumptruck style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }} />
    );
    if (vehicleData.vehicleType === "DEX")
      tempIcon = (
        <Excavator style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }} />
      );
    else if (vehicleData.vehicleType === "CRW")
      tempIcon = (
        <Crawler style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }} />
      );
    else if (vehicleData.vehicleType === "CTL")
      tempIcon = (
        <SkidSteer style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }} />
      );
    else if (vehicleData.vehicleType === "TRK")
      tempIcon = (
        <Truck style={{ minHeight: "32px", minWidth: "48px", height: "auto", width: "auto" }} />
      );
    else if (vehicleData.vehicleType === "WDL")
      tempIcon = (
        <Loader style={{ minHeight: "48px", minWidth: "48px", height: "auto", width: "auto" }} />
      );
    const tempAutonomyStatus = (
      <div
        style={{
          position: "relative",
          left: "-92px",
          width: "200px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <b style={{ height: "16px" }}>{vehicleData.vehicleType + " " + vehicleData.vehicleName}</b>
        <div style={{ height: "18px" }}>{vehicleData.vehicleModel}</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {displayState === "off" && (
            <div
              style={{
                width: "68px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                marginRight: "-23px",
              }}
            >
              <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
              Off
            </div>
          )}
          {displayState === "ready" && (
            <div
              style={{
                width: "60px",
                color: "#00B600",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              <StatusAutonomyReady size={20} />
              Ready
            </div>
          )}
          {displayState === "paused" && (
            <div
              style={{
                color: "rgb(255, 120, 0)",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
              <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
              Paused
            </div>
          )}
          {displayState === "working" && (
            <div
              style={{
                color: "#00B600",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
              <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
              Working
            </div>
          )}
          <div style={{ marginLeft: "6px", color: "#767676" }}>{formattedTime}</div>
        </div>
      </div>
    );
    let vehicleZoomBounds = undefined;
    if (lat !== null && lng !== null) {
      vehicleZoomBounds = new google.maps.LatLngBounds();
      vehicleZoomBounds.extend(new google.maps.LatLng(lat + 0.0005, lng + 0.0005));
      vehicleZoomBounds.extend(new google.maps.LatLng(lat - 0.0005, lng - 0.0005));
    }
    return {
      id: key,
      extraContent: tempAutonomyStatus,
      icon: tempIcon,
      vehiclezoombounds: vehicleZoomBounds,
      latlng: lat !== null && lng !== null ? { lat, lng } : undefined,
    };
  }

  useEffect(() => {
    if (vehicleIconClicked && vehicleData) {
      const item = prepareVehicleItem(vehicleData);
      setCurrent(item, 0);
      setCurrentEquipment(item);
    }
    // ...existing code...
  }, [vehicleIconClicked, vehicleData]);

  useEffect(() => {
    if (map && currentEquipment?.vehiclezoombounds && !showCabControl) {
      map.fitBounds(currentEquipment.vehiclezoombounds);
    }
  }, [map, currentEquipment, showCabControl]);

  return (
    <div className="JobsiteList">
      <div className="SearchEntry">
        <SearchField
          color="primary"
          onBlur={function noRefCheck() {}}
          onChange={searchEquipment}
          onFocus={function noRefCheck() {}}
          onInvalid={function noRefCheck() {}}
          onKeyDown={function noRefCheck() {}}
          onKeyUp={function noRefCheck() {}}
          size="medium"
          style={{ width: "340px", height: "32px" }}
        />
        <IconButton
          icon={<IconAdd />}
          onFocusVisible={function noRefCheck() {}}
          title="Add a Machine"
          style={{ width: "32px", height: "32px" }}
        />
      </div>
      <div className="FilterChips">
        <MultiselectChip
          emptyListLabel="No Data"
          items={[
            {
              icon: <Dumptruck />,
              id: "0",
              title: "ADT",
            },
            {
              icon: <Excavator />,
              id: "1",
              title: "DEX",
            },
            {
              icon: <Crawler />,
              id: "2",
              title: "CRW",
            },
            {
              icon: <SkidSteer />,
              id: "3",
              title: "CTL",
            },
            {
              icon: <Truck />,
              id: "4",
              title: "TRK",
            },
            {
              icon: <Loader />,
              id: "5",
              title: "WDL",
            },
          ]}
          noItemMatchLabel="No machines matched your search."
          onChange={function noRefCheck() {}}
          pluralTitle="Makes"
          selectAllItemLabel="Select All"
          selectedIds={[]}
          title="Make"
        />
        <MultiselectChip
          emptyListLabel="No Data"
          items={[
            {
              icon: <Dumptruck />,
              id: "0",
              title: "ADT",
            },
            {
              icon: <Loader />,
              id: "0",
              title: "Loader",
            },
          ]}
          noItemMatchLabel="No machines matched your search."
          onChange={function noRefCheck() {}}
          pluralTitle="Types"
          selectAllItemLabel="Select All"
          selectedIds={[]}
          title="Type"
        />
        <MultiselectChip
          emptyListLabel="No Data"
          items={[
            {
              icon: <Dumptruck />,
              id: "0",
              title: "ADT",
            },
            {
              icon: <Loader />,
              id: "0",
              title: "Loader",
            },
          ]}
          noItemMatchLabel="No machines matched your search."
          onChange={function noRefCheck() {}}
          pluralTitle="Models"
          selectAllItemLabel="Select All"
          selectedIds={[]}
          title="Model"
        />
        <MultiselectChip
          emptyListLabel="No Data"
          items={[
            {
              icon: <Dumptruck />,
              id: "0",
              title: "ADT",
            },
            {
              icon: <Loader />,
              id: "0",
              title: "Loader",
            },
          ]}
          noItemMatchLabel="No machines matched your search."
          onChange={function noRefCheck() {}}
          pluralTitle="Tags"
          selectAllItemLabel="Select All"
          selectedIds={[]}
          title="Tag"
        />
        <MoreFiltersChip onClick={function noRefCheck() {}} variant="small" />
      </div>
      <div className="PanelList">
        <GroupedPanelList groups={groups} />
      </div>
    </div>
  );
}

export default EquipmentList;
